import { useState, useCallback, useEffect, useRef } from 'react';

/**
 * Headless presentation/fullscreen hook.
 * Handles native Fullscreen API entry/exit and keeps the React `presenting`
 * state in sync when the user exits fullscreen via Esc or browser controls.
 */
export function useFullscreen() {
  const [presenting, setPresenting] = useState(false);
  const targetRef = useRef(null);

  const enter = useCallback(async () => {
    setPresenting(true);
    // Wait one frame for the overlay to mount so targetRef is bound
    await new Promise((r) => requestAnimationFrame(r));
    try {
      const el = targetRef.current || document.documentElement;
      if (el.requestFullscreen) await el.requestFullscreen();
      else if (el.webkitRequestFullscreen) await el.webkitRequestFullscreen();
    } catch (err) { console.error('Fullscreen enter failed:', err); }
  }, []);

  const exit = useCallback(async () => {
    setPresenting(false);
    try {
      if (document.fullscreenElement && document.exitFullscreen) await document.exitFullscreen();
      else if (document.webkitFullscreenElement && document.webkitExitFullscreen) await document.webkitExitFullscreen();
    } catch (err) { console.error('Fullscreen exit failed:', err); }
  }, []);

  useEffect(() => {
    const sync = () => {
      const fsEl = document.fullscreenElement || document.webkitFullscreenElement;
      if (!fsEl) setPresenting(false);
    };
    document.addEventListener('fullscreenchange', sync);
    document.addEventListener('webkitfullscreenchange', sync);
    return () => {
      document.removeEventListener('fullscreenchange', sync);
      document.removeEventListener('webkitfullscreenchange', sync);
    };
  }, []);

  return { presenting, targetRef, enter, exit };
}
